/**
 * RecentSessionsSection Component
 * Displays recent deep analysis sessions as cards with a link to full history
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, DeepAnalysisHistoryItem } from '../../services/api';
import { SessionCard } from './SessionCard';

interface RecentSessionsSectionProps {
  onSessionSelect: (sessionId: string) => void;
  loadingSessionId?: string | null;
  maxSessions?: number;
}

export function RecentSessionsSection({
  onSessionSelect,
  loadingSessionId,
  maxSessions = 3,
}: RecentSessionsSectionProps) {
  const [sessions, setSessions] = useState<DeepAnalysisHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        const response = await api.getDeepAnalysisHistory({ limit: maxSessions });
        // Map the response to match DeepAnalysisHistoryItem type
        const mappedSessions: DeepAnalysisHistoryItem[] = response.sessions.map(s => ({
          id: s.id,
          status: s.status,
          phase: s.phase,
          mode: s.mode,
          documentPreview: (s as any).documentPreview || '',
          tokensTotal: (s as any).tokensTotal || 0,
          createdAt: s.createdAt,
          completedAt: s.completedAt,
          durationMs: s.durationMs,
          hasReport: s.hasReport,
        }));
        setSessions(mappedSessions);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Błąd ładowania sesji');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, [maxSessions]);

  // Don't show section if no sessions and no error
  if (!isLoading && sessions.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="font-medium text-slate-900 dark:text-white">Ostatnie analizy</h3>
          {sessions.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded">
              {sessions.length}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="w-6 h-6 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          ) : error ? (
            <div className="py-4 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : (
            <>
              {/* Session cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {sessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    onClick={onSessionSelect}
                    isLoading={loadingSessionId === session.id}
                  />
                ))}
              </div>

              {/* Link to full history */}
              <Link
                to="/analysis/history"
                className="flex items-center justify-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
              >
                <span>Zobacz wszystkie analizy</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RecentSessionsSection;
